import * as React from 'react'

import OverviewSection from './overviewSection'
import SkillSection from './skillSection'
import SkillStatusPerStudentSection, { POST_SKILL_STATUS_OPTIONS } from './skillStatusPerStudentSection'
import StudentSection from './studentSection'

import { FULL, restrictedPage, OVERVIEW, SKILL, SKILL_STATUS_PER_STUDENT, STUDENT, mapItemsIfNotAll, groupByDropdownOptions, getDiagnosticTypeDropdownOptions } from '../../shared'
import { ButtonSpinner, DropdownInput, Snackbar, Spinner, SegmentedControl, filterIcon, defaultSnackbarTimeout, documentFileIcon, whiteEmailIcon, getPageTitle, EXTRA_SMALL_ICON_BASE_SRC } from '../../../Shared/index'
import useSnackbarMonitor from '../../../Shared/hooks/useSnackbarMonitor';
import useWindowSize from '../../../Shared/hooks/useWindowSize';
import { DropdownObjectInterface } from '../../../Staff/interfaces/evidenceInterfaces'
import { requestDelete, requestGet, requestPost } from '../../../../modules/request'
import ReportSubscriptionModal from '../../components/usage_snapshots/reportSubscriptionModal';

const barChartGreySrc = `${window.postbuildEnv.CDN_URL}/images/pages/diagnostic_reports/icons-bar-chart.svg`
const barChartWhiteIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/white-bar-chart-icon.svg`
const groupOfStudentsGreyIconSrc = `${window.postbuildEnv.CDN_URL}/images/pages/administrator/usage_snapshot_report/students.svg`
const groupOfStudentsWhiteIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/students-white.svg`
const pencilGreyIconSrc = `${window.postbuildEnv.CDN_URL}/images/pages/administrator/usage_snapshot_report/pencil.svg`
const pencilWhiteIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/white-pencil-icon.svg`
const outlinedStarIconSrc = `${window.postbuildEnv.CDN_URL}/images/pages/administrator/usage_snapshot_report/outlined_star.svg`
const outlinedStarWhiteIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/xs/star-white.svg`

const ADMIN_DIAGNOSTIC_SUBSCRIPTION = 'admin_diagnostic_report'
const FILTER_SELECTIONS_REPORT_BASE = 'diagnostic_growth_report_'
const MAX_VIEW_WIDTH_FOR_SEGMENTED_CONTROL = 1400

// Tab configuration - single source of truth for tab constants and display names
const TAB_CONFIG = [
  { tab: OVERVIEW, displayName: 'Performance & Growth Overview' },
  { tab: SKILL, displayName: 'Overall Skill Growth' },
  { tab: STUDENT, displayName: 'Overall Student Growth' },
  { tab: SKILL_STATUS_PER_STUDENT, displayName: 'Skill Status Per Student' },
] as const

// Derive mappings from single source of truth
const displayNameToTab = Object.fromEntries(
  TAB_CONFIG.map(({ tab, displayName }) => [displayName, tab])
)

const tabToDisplayName = Object.fromEntries(
  TAB_CONFIG.map(({ tab, displayName }) => [tab, displayName])
)

// Display name constants for external use
const OVERVIEW_DISPLAY = TAB_CONFIG[0].displayName
const SKILL_STATUS_PER_STUDENT_DISPLAY = TAB_CONFIG[3].displayName

// Icon configuration for each tab
const TAB_ICONS = {
  [OVERVIEW]: { inactiveIconSrc: barChartGreySrc, activeIconSrc: barChartWhiteIconSrc },
  [SKILL]: { inactiveIconSrc: pencilGreyIconSrc, activeIconSrc: pencilWhiteIconSrc },
  [STUDENT]: { inactiveIconSrc: groupOfStudentsGreyIconSrc, activeIconSrc: groupOfStudentsWhiteIconSrc },
  [SKILL_STATUS_PER_STUDENT]: { inactiveIconSrc: outlinedStarIconSrc, activeIconSrc: outlinedStarWhiteIconSrc },
}

// Build reportButtons from TAB_CONFIG and TAB_ICONS
const reportButtons = TAB_CONFIG.map(({ tab, displayName }) => ({
  tab,
  label: displayName,
  ...TAB_ICONS[tab]
}))

export const DiagnosticGrowthReportsContainer = ({
  accessType,
  loadingFilters,
  pusherChannel,
  searchCount,
  selectedClassrooms,
  availableClassrooms,
  selectedTimeframe,
  selectedGrades,
  availableGrades,
  selectedSchools,
  selectedTeachers,
  availableTeachers,
  openMobileFilterMenu,
  hasAdjustedFiltersFromDefault,
  handleSetDiagnosticIdForStudentCount,
  passedData,
  horizontalFilters
}) => {

  document.title = getPageTitle("Premium Hub", "Premium Reports | Diagnostic Growth Report")
  const [activeTab, setActiveTab] = React.useState<string>(OVERVIEW_DISPLAY)
  const currentTab = displayNameToTab[activeTab]
  const [selectedDiagnosticType, setSelectedDiagnosticType] = React.useState<DropdownObjectInterface>(null)
  const [selectedGroupByValue, setSelectedGroupByValue] = React.useState<DropdownObjectInterface>(null)
  const [selectedSkillValue, setSelectedSkillValue] = React.useState<DropdownObjectInterface>(null)
  const [selectedPostSkillStatuses, setSelectedPostSkillStatuses] = React.useState<DropdownObjectInterface[]>([])
  const skipFilterLoadRef = React.useRef(false)
  const [noDiagnosticDataAvailable, setNoDiagnosticDataAvailable] = React.useState<boolean>(!!passedData)
  const [downloadButtonBusy, setDownloadButtonBusy] = React.useState<boolean>(false)
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarCopy, setSnackbarCopy] = React.useState<boolean>(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = React.useState(false)
  const [currentEmailSubscription, setCurrentEmailSubscription] = React.useState(null)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const size = useWindowSize()

  React.useEffect(() => {
    requestGet(`/email_subscriptions/${ADMIN_DIAGNOSTIC_SUBSCRIPTION}/current`, (body) => {
      setCurrentEmailSubscription(body)
    })
  }, [])

  React.useEffect(() => {
    // this is for testing purposes; this value will always be null in a non-testing environment
    if (!passedData) {
      if (skipFilterLoadRef.current) {
        skipFilterLoadRef.current = false
        return
      }
      getFilterSelections(currentTab)
    }
  }, [activeTab])

  React.useEffect(() => {
    const canSave = currentTab === SKILL_STATUS_PER_STUDENT
      ? selectedDiagnosticType
      : selectedGroupByValue && selectedDiagnosticType
    if (!passedData && canSave) {
      saveFilterSelections(currentTab)
    }
  }, [selectedGroupByValue, selectedDiagnosticType])

  const sharedProps = {
    searchCount,
    selectedGrades: mapItemsIfNotAll(selectedGrades, availableGrades, 'value'),
    selectedSchoolIds: selectedSchools.map(school => school.id),
    selectedTeacherIds: mapItemsIfNotAll(selectedTeachers, availableTeachers),
    selectedClassroomIds: mapItemsIfNotAll(selectedClassrooms, availableClassrooms),
    selectedTimeframe: selectedTimeframe.value,
    pusherChannel,
    hasAdjustedFiltersFromDefault,
    handleSetNoDiagnosticDataAvailable,
    handleSetDiagnosticIdForStudentCount,
    passedData: null,
    horizontalFilters
  }

  const overviewProps = {
    ...sharedProps,
    groupByValue: selectedGroupByValue,
    handleSetSelectedDiagnosticType,
    handleTabChangeFromDataChip,
    handleSetSelectedGroupByValue
  }

  const skillSectionProps = {
    ...sharedProps,
    groupByValue: selectedGroupByValue,
    diagnosticTypeValue: selectedDiagnosticType,
    handleSetSelectedDiagnosticType,
    handleSetSelectedGroupByValue,
    handleNavigateToSkillStatusPerStudent
  }

  const skillStatusPerStudentSectionProps = {
    ...sharedProps,
    diagnosticTypeValue: selectedDiagnosticType,
    selectedSkillValue,
    selectedPostSkillStatuses,
    handleSetSelectedDiagnosticType,
    handleSetSelectedSkillValue,
    handleSetSelectedPostSkillStatuses,
    createCsvReportDownload
  }

  const studentSectionProps = {
    ...sharedProps,
    createCsvReportDownload: createCsvReportDownload,
    diagnosticTypeValue: selectedDiagnosticType,
    handleSetSelectedDiagnosticType,
    passedStudentData: null,
    passedRecommendationsData: null,
    passedVisibleData: null
  }

  function showInSnackbar(snackbarCopy: string) {
    setSnackbarCopy(snackbarCopy)
    setShowSnackbar(true)
  }

  function getFilterSelections(tab) {
    requestPost('/admin_report_filter_selections/show', { report: `${FILTER_SELECTIONS_REPORT_BASE}${tab}` }, (selections) => {
      if (selections) {
        const { group_by_value, diagnostic_type_value } = selections.filter_selections
        if (tab !== SKILL_STATUS_PER_STUDENT) { setSelectedGroupByValue(group_by_value) }
        setSelectedDiagnosticType(diagnostic_type_value)
      } else {
        if (tab !== SKILL_STATUS_PER_STUDENT) { setSelectedGroupByValue(groupByDropdownOptions[0]) }
        setSelectedDiagnosticType(getDiagnosticTypeDropdownOptions(selectedTimeframe.value)[0])
      }
    })
  }

  function saveFilterSelections(tab, successCallback = () => { }) {
    const filterSelections = tab === SKILL_STATUS_PER_STUDENT
      ? { diagnostic_type_value: selectedDiagnosticType }
      : { group_by_value: selectedGroupByValue, diagnostic_type_value: selectedDiagnosticType }

    postFilterSelections(tab, filterSelections, successCallback)
  }

  function postFilterSelections(tab, filterSelections, successCallback = () => { }) {
    const params = {
      admin_report_filter_selection: {
        filter_selections: filterSelections,
        report: `${FILTER_SELECTIONS_REPORT_BASE}${tab}`
      }
    }

    requestPost('/admin_report_filter_selections/create_or_update', params, successCallback)
  }

  function resetTabState(newTab: string) {
    setActiveTab(newTab)
    setSelectedGroupByValue(null)
    setSelectedDiagnosticType(null)
    setSelectedSkillValue(null)
    setSelectedPostSkillStatuses([])
  }

  function handleTabChange(e) {
    resetTabState(e.currentTarget.value)
  }

  function handleTabChangeFromDropdown(option) {
    resetTabState(option.value)
  }

  function handleTabChangeFromDataChip(value) {
    setActiveTab(tabToDisplayName[value])
  }

  function handleSetNoDiagnosticDataAvailable(value: boolean) {
    setNoDiagnosticDataAvailable(value)
  }

  function handleSetSelectedDiagnosticType(value: DropdownObjectInterface) {
    setSelectedDiagnosticType(value)
  }

  function handleSetSelectedGroupByValue(value: DropdownObjectInterface) {
    setSelectedGroupByValue(value)
  }

  function handleSetSelectedSkillValue(value: DropdownObjectInterface) {
    setSelectedSkillValue(value)
  }

  function handleSetSelectedPostSkillStatuses(values: DropdownObjectInterface[]) {
    setSelectedPostSkillStatuses(values)
  }

  function handleNavigateToSkillStatusPerStudent(skillGroupId, postSkillStatusValues) {
    skipFilterLoadRef.current = true
    setActiveTab(SKILL_STATUS_PER_STUDENT_DISPLAY)
    setSelectedSkillValue({ label: '', value: skillGroupId })
    setSelectedPostSkillStatuses(postSkillStatusValues.map(v => POST_SKILL_STATUS_OPTIONS.find(o => o.value === v)).filter(Boolean))
  }

  function createCsvReportDownload() {
    const buttonDisableTime = 2000
    setDownloadButtonBusy(true)

    requestPost('/admin_diagnostic_reports/download', {}, (body) => {
      showInSnackbar("You will receive an email with a download link shortly.")
      setTimeout(() => { setDownloadButtonBusy(false) }, buttonDisableTime);
    })
  }

  function handleClickSubscribe() {
    setIsSubscriptionModalOpen(!isSubscriptionModalOpen);
  }

  function handleSubscriptionCancel() {
    setIsSubscriptionModalOpen(false);
  }

  function handleSubscriptionSave(isSubscribed, frequency) {
    setIsSubscriptionModalOpen(false);
    if (isSubscribed) {
      createOrUpdateEmailSubscription(frequency)
    } else if (currentEmailSubscription) {
      deleteEmailSubscription()
    }
    showInSnackbar('Subscription settings saved')
  }

  function createOrUpdateEmailSubscription(frequency) {
    const emailSubscriptionParams = {
      subscription: {
        frequency
      }
    }

    requestPost(`/email_subscriptions/${ADMIN_DIAGNOSTIC_SUBSCRIPTION}/create_or_update`, emailSubscriptionParams, (emailSubscription) => {
      setCurrentEmailSubscription(emailSubscription)
    })
  }

  function deleteEmailSubscription() {
    requestDelete(`/email_subscriptions/${ADMIN_DIAGNOSTIC_SUBSCRIPTION}`, {}, () => setCurrentEmailSubscription(null), error => { throw (error) })
  }

  const segmentedControlButtons = reportButtons.map(button => ({
    label: button.label,
    activeIconSrc: button.activeIconSrc,
    inactiveIconSrc: button.inactiveIconSrc,
    onClick: handleTabChange
  }))

  const tabDropdownOptions = reportButtons.map(button => ({
    value: button.label,
    label: button.label
  }))

  const tabDropdown = (
    <DropdownInput
      ariaLabel="Report view"
      handleChange={handleTabChangeFromDropdown}
      id="tab-dropdown"
      isSearchable={false}
      label=""
      options={tabDropdownOptions}
      value={tabDropdownOptions.find(opt => opt.value === activeTab)}
    />
  )

  function renderContent() {
    /* TEMPORARILY DISABLING THIS CODE
       TODO: re-enable this once we get code in place so that the "Apply filters" button will actually apply filters if this cut-out view is showing
    if (noDiagnosticDataAvailable) {
      return(
        <div className="no-diagnostic-data-container">
          <h2>There are not yet any completed diagnostics.</h2>
          <p>The Quill Diagnostic enables you to provide a Pre-Diagnostic at the start of the year to identify skills students need to practice. The Post-Diagnostic allows you to then measure students&apos; learning gains over the course of the school year.</p>
          <p>At the moment, there are no teachers connected to your admin account who have completed a diagnostic. Once at least one teacher has completed a diagnostic with at least one student, you will be able to see their results in this report. View our guide to learn how teachers can assign diagnostics to their students.</p>
          <a className="assign-link focus-on-light" href="https://support.quill.org/en/articles/1049933-how-do-i-assign-a-quill-diagnostic" rel="noopener noreferrer" target="_blank">How to Assign a Diagnostic</a>
        </div>
      )
    }
    */
    return (
      <React.Fragment>
        {size.width >= MAX_VIEW_WIDTH_FOR_SEGMENTED_CONTROL ? (
          <SegmentedControl
            activeTab={activeTab}
            buttons={segmentedControlButtons}
            label="Report view:"
            size="small"
          />
        ) : tabDropdown}
        <div className="filter-button-container">
          <button className="interactive-wrapper focus-on-light" onClick={openMobileFilterMenu} type="button">
            <img alt={filterIcon.alt} src={filterIcon.src} />
            Filters
          </button>
        </div>
        {currentTab === OVERVIEW && <OverviewSection {...overviewProps} />}
        {currentTab === SKILL && <SkillSection {...skillSectionProps} />}
        {currentTab === STUDENT && <StudentSection {...studentSectionProps} />}
        {currentTab === SKILL_STATUS_PER_STUDENT && <SkillStatusPerStudentSection {...skillStatusPerStudentSectionProps} />}
      </React.Fragment>
    )
  }

  if (loadingFilters) {
    return <Spinner />
  }

  if (accessType !== FULL) {
    return restrictedPage
  }

  return (
    <main>
      <div className="header">
        <ReportSubscriptionModal
          cancel={handleSubscriptionCancel}
          currentSubscription={currentEmailSubscription}
          isOpen={isSubscriptionModalOpen}
          save={handleSubscriptionSave}
        />
        <Snackbar text={snackbarCopy} visible={showSnackbar} />
        <h1>
          Diagnostic Growth Report
        </h1>
        <div className="header-buttons">
          <a className="quill-button outlined small grey focus-on-light" href="https://support.quill.org/en/articles/9084379-how-do-i-navigate-the-diagnostic-growth-report-in-the-premium-hub" rel="noopener noreferrer" target="_blank">
            <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/file-document.svg`} />
            <span>Guide</span>
          </a>
          <button
            className="quill-button outlined small grey focus-on-light"
            onClick={handleClickSubscribe}
            type="button"
          >
            <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/email.svg`} />
            <span>Subscribe</span>
          </button>
          <button className="quill-button outlined small grey focus-on-light" onClick={createCsvReportDownload} type="button">
            {downloadButtonBusy ? <ButtonSpinner variant="dark" /> : <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />}
            <span>Download</span>
          </button>
        </div>
      </div>
      {renderContent()}
    </main>
  )
}

export default DiagnosticGrowthReportsContainer
